/**
 * Google Tag Manager Tracking Service
 * Centralized GTM event tracking for Rulevia application
 * Handles all conversion tracking and user analytics
 * Integrates with Cookie Consent for GDPR compliance
 */

import type {
  GTMUserProperties,
  GTMSignUpEvent,
  GTMLoginEvent,
  GTMLogoutEvent,
  GTMPurchaseEvent,
  GTMBeginCheckoutEvent,
  GTMViewItemEvent,
  GTMAnalysisEvent,
  GTMProjectEvent,
  GTMTeamEvent,
  GTMSearchEvent,
  GTMReportEvent,
  GTMPageViewEvent,
  GTMErrorEvent,
  GTMContentEvent,
  GTMEngagementEvent,
  GTMReferralEvent,
  GTMVersioningEvent,
  GTMEcommerceItem,
  AllGTMEvents
} from '../types/gtm';

// GTM Configuration
export const GTM_CONFIG = {
  containerId: 'GTM-MP6JXGS4',
  enabled: typeof window !== 'undefined' && window.location.hostname !== 'localhost'
};

// Consent categories mapped to event types
const CONSENT_REQUIREMENTS = {
  analytics: ['page_view', 'search', 'user_engagement', 'exception'],
  marketing: ['purchase', 'begin_checkout', 'view_item', 'sign_up', 'login']
} as const;

// Initialize dataLayer if not exists
function initializeDataLayer(): void {
  if (typeof window !== 'undefined' && !window.dataLayer) {
    window.dataLayer = [];
  }
}

// Check if consent is granted for a specific category
function hasConsent(category: 'analytics' | 'marketing'): boolean {
  try {
    // Try to get consent from cookie consent service
    const cookieConsent = (window as any).__cookieConsent;
    if (cookieConsent && typeof cookieConsent.hasConsent === 'function') {
      return cookieConsent.hasConsent(category);
    }

    // Fallback: check for consent cookie directly
    const consentCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('cc_cookie='));
    
    if (consentCookie) {
      const consentValue = consentCookie.split('=')[1];
      try {
        const parsed = JSON.parse(decodeURIComponent(consentValue));
        return parsed.categories?.includes(category) || false;
      } catch {
        return false;
      }
    }

    // Default: no consent
    return false;
  } catch (error) {
    console.warn('[GTM] Failed to check consent, defaulting to false:', error);
    return false;
  }
}

// Check if an event requires consent and if consent is granted
function canSendEvent(eventName: string): boolean {
  // Essential events (authentication, navigation, etc.) always allowed
  const essentialEvents = [
    'user_properties_set',
    'login',
    'logout',
    'sign_up',
    'create_project',
    'delete_project',
    'invite_team_member',
    'accept_invitation'
  ];

  if (essentialEvents.includes(eventName)) {
    return true;
  }

  // Check analytics consent
  if (CONSENT_REQUIREMENTS.analytics.includes(eventName as any)) {
    return hasConsent('analytics');
  }

  // Check marketing consent
  if (CONSENT_REQUIREMENTS.marketing.includes(eventName as any)) {
    return hasConsent('marketing');
  }

  // Default: allow if no specific requirement
  return true;
}

// Push event to dataLayer with consent checking
function pushToDataLayer(event: AllGTMEvents): void {
  if (!GTM_CONFIG.enabled) {
    return;
  }

  // Check consent before sending
  if (!canSendEvent(event.event)) {
    return;
  }

  initializeDataLayer();
  
  try {
    window.dataLayer.push(event);
    console.log('[GTM] Event pushed:', event.event);
  } catch (error) {
    console.error('[GTM] Error pushing event:', error);
  }
}

// Hash user ID for privacy
function hashUserId(userId: string): string {
  // Simple hash for user ID privacy - in production use proper hashing
  return btoa(userId).replace(/[+=]/g, '').substring(0, 16);
}

// GTM Service Class
export class GTMService {
  
  // ========== INITIALIZATION ==========
  
  /**
   * Initialize GTM with user properties
   */
  static initialize(userProperties?: GTMUserProperties): void {
    if (!GTM_CONFIG.enabled) return;

    initializeDataLayer();
    
    if (userProperties) {
      this.setUserProperties(userProperties);
    }
  }

  /**
   * Register cookie consent service for consent checking
   */
  static registerConsentService(consentService: any): void {
    if (typeof window !== 'undefined') {
      (window as any).__cookieConsent = consentService;
    }
  }

  /**
   * Check if analytics consent is granted
   */
  static hasAnalyticsConsent(): boolean {
    return hasConsent('analytics');
  }

  /**
   * Check if marketing consent is granted
   */
  static hasMarketingConsent(): boolean {
    return hasConsent('marketing');
  }

  /**
   * Set user properties for tracking
   */
  static setUserProperties(properties: GTMUserProperties): void {
    const safeProperties = {
      ...properties,
      user_id: properties.user_id ? hashUserId(properties.user_id) : undefined
    };

    pushToDataLayer({
      event: 'user_properties_set',
      ...safeProperties
    });
  }

  // ========== PAGE TRACKING ==========
  
  /**
   * Track page views
   */
  static trackPageView(pageTitle?: string, userId?: string): void {
    const event: GTMPageViewEvent = {
      event: 'page_view',
      page_title: pageTitle || document.title,
      page_location: window.location.href,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  // ========== AUTHENTICATION EVENTS ==========
  
  /**
   * Track user registration
   */
  static trackSignUp(method: string = 'email', userId?: string): void {
    const event: GTMSignUpEvent = {
      event: 'sign_up',
      method,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  /**
   * Track user login
   */
  static trackLogin(method: string = 'email', userId?: string): void {
    const event: GTMLoginEvent = {
      event: 'login',
      method,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  /**
   * Track user logout
   */
  static trackLogout(userId?: string): void {
    const event: GTMLogoutEvent = {
      event: 'logout',
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  // ========== ECOMMERCE EVENTS ==========
  
  /**
   * Track subscription plan viewing
   */
  static trackViewPlans(userId?: string): void {
    const items: GTMEcommerceItem[] = [
      {
        item_id: 'starter_plan',
        item_name: 'Starter Plan',
        category: 'subscription',
        price: 15.90,
        currency: 'USD',
        quantity: 1
      },
      {
        item_id: 'pro_plan',
        item_name: 'Pro Plan',
        category: 'subscription',
        price: 39.90,
        currency: 'USD',
        quantity: 1
      }
    ];

    const event: GTMViewItemEvent = {
      event: 'view_item',
      currency: 'USD',
      value: 39.90, // Highest value plan
      items,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  /**
   * Track checkout initiation
   */
  static trackBeginCheckout(planName: string, price: number, userId?: string): void {
    const item: GTMEcommerceItem = {
      item_id: planName.toLowerCase().replace(' ', '_'),
      item_name: planName,
      category: 'subscription',
      price,
      currency: 'USD',
      quantity: 1
    };

    const event: GTMBeginCheckoutEvent = {
      event: 'begin_checkout',
      currency: 'USD',
      value: price,
      items: [item],
      user_id: userId ? hashUserId(userId) : undefined
    };

    console.log('🛒 GTM Begin Checkout Event:', {
      event: event.event,
      value: event.value,
      plan_name: planName,
      user_id_hashed: event.user_id
    });

    pushToDataLayer(event);
  }

  /**
   * Track successful purchase
   */
  static trackPurchase(
    transactionId: string,
    planName: string,
    price: number,
    userId?: string,
    subscriptionType: 'new' | 'upgrade' | 'renewal' = 'new'
  ): void {
    const item: GTMEcommerceItem = {
      item_id: planName.toLowerCase().replace(' ', '_'),
      item_name: planName,
      category: 'subscription',
      price,
      currency: 'USD',
      quantity: 1
    };

    const event: GTMPurchaseEvent = {
      event: 'purchase',
      transaction_id: transactionId,
      value: price,
      currency: 'USD',
      items: [item],
      user_id: userId ? hashUserId(userId) : undefined,
      subscription_type: subscriptionType
    };

    console.log('🛒 GTM Purchase Event:', {
      event: event.event,
      transaction_id: event.transaction_id,
      value: event.value,
      plan_name: planName,
      subscription_type: subscriptionType,
      user_id_hashed: event.user_id
    });

    pushToDataLayer(event);
  }

  /**
   * Track credit purchase
   */
  static trackCreditPurchase(
    transactionId: string,
    creditAmount: number,
    price: number,
    userId?: string
  ): void {
    const item: GTMEcommerceItem = {
      item_id: `credits_${creditAmount}`,
      item_name: `${creditAmount} Credits`,
      category: 'credits',
      price,
      currency: 'USD',
      quantity: 1
    };

    const event: GTMPurchaseEvent = {
      event: 'purchase',
      transaction_id: transactionId,
      value: price,
      currency: 'USD',
      items: [item],
      user_id: userId ? hashUserId(userId) : undefined,
      subscription_type: 'credits'
    };

    pushToDataLayer(event);
  }

  // ========== ANALYSIS EVENTS ==========
  
  /**
   * Track analysis start
   */
  static trackAnalysisStart(
    analysisType: string,
    fileType: string,
    fileSize: number,
    platforms: string[] = [],
    userId?: string,
    projectId?: string
  ): void {
    const event: GTMAnalysisEvent = {
      event: 'start_analysis',
      analysis_type: analysisType,
      file_type: fileType,
      file_size: fileSize,
      platforms,
      user_id: userId ? hashUserId(userId) : undefined,
      project_id: projectId
    };

    pushToDataLayer(event);
  }

  /**
   * Track analysis completion
   */
  static trackAnalysisComplete(
    analysisType: string,
    fileType: string,
    platforms: string[] = [],
    userId?: string,
    projectId?: string
  ): void {
    const event: GTMAnalysisEvent = {
      event: 'complete_analysis',
      analysis_type: analysisType,
      file_type: fileType,
      platforms,
      user_id: userId ? hashUserId(userId) : undefined,
      project_id: projectId
    };

    pushToDataLayer(event);
  }

  /**
   * Track analysis error
   */
  static trackAnalysisError(
    analysisType: string,
    errorMessage: string,
    userId?: string,
    projectId?: string
  ): void {
    const event: GTMAnalysisEvent = {
      event: 'analysis_error',
      analysis_type: analysisType,
      custom_parameter_1: errorMessage,
      user_id: userId ? hashUserId(userId) : undefined,
      project_id: projectId
    };

    pushToDataLayer(event);
  }

  // ========== PROJECT EVENTS ==========
  
  /**
   * Track project creation
   */
  static trackProjectCreate(
    projectId: string,
    projectName: string,
    userId?: string,
    organizationId?: string
  ): void {
    const event: GTMProjectEvent = {
      event: 'create_project',
      project_id: projectId,
      project_name: projectName,
      user_id: userId ? hashUserId(userId) : undefined,
      organization_id: organizationId
    };

    pushToDataLayer(event);
  }

  /**
   * Track project deletion
   */
  static trackProjectDelete(
    projectId: string,
    projectName: string,
    userId?: string,
    organizationId?: string
  ): void {
    const event: GTMProjectEvent = {
      event: 'delete_project',
      project_id: projectId,
      project_name: projectName,
      user_id: userId ? hashUserId(userId) : undefined,
      organization_id: organizationId
    };

    pushToDataLayer(event);
  }

  // ========== TEAM EVENTS ==========
  
  /**
   * Track team member invitation
   */
  static trackTeamInvite(
    organizationId: string,
    userRole: string,
    userId?: string
  ): void {
    const event: GTMTeamEvent = {
      event: 'invite_team_member',
      organization_id: organizationId,
      user_role: userRole,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  /**
   * Track invitation acceptance
   */
  static trackInvitationAccept(
    organizationId: string,
    userRole: string,
    userId?: string
  ): void {
    const event: GTMTeamEvent = {
      event: 'accept_invitation',
      organization_id: organizationId,
      user_role: userRole,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  // ========== SEARCH & CONTENT EVENTS ==========
  
  /**
   * Track search queries
   */
  static trackSearch(
    searchTerm: string,
    resultsCount: number = 0,
    userId?: string
  ): void {
    const event: GTMSearchEvent = {
      event: 'search',
      search_term: searchTerm,
      results_count: resultsCount,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  /**
   * Track report generation
   */
  static trackReportGenerate(
    reportType: string,
    dateRange?: string,
    userId?: string,
    organizationId?: string
  ): void {
    const event: GTMReportEvent = {
      event: 'generate_report',
      report_type: reportType,
      date_range: dateRange,
      user_id: userId ? hashUserId(userId) : undefined,
      organization_id: organizationId
    };

    pushToDataLayer(event);
  }

  /**
   * Track report export
   */
  static trackReportExport(
    reportType: string,
    exportFormat: string,
    userId?: string,
    organizationId?: string
  ): void {
    const event: GTMReportEvent = {
      event: 'export_report',
      report_type: reportType,
      export_format: exportFormat,
      user_id: userId ? hashUserId(userId) : undefined,
      organization_id: organizationId
    };

    pushToDataLayer(event);
  }

  // ========== ERROR TRACKING ==========
  
  /**
   * Track application errors
   */
  static trackError(
    errorDescription: string,
    fatal: boolean = false,
    userId?: string
  ): void {
    const event: GTMErrorEvent = {
      event: 'exception',
      description: errorDescription,
      fatal,
      page_location: window.location.href,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  // ========== ENGAGEMENT EVENTS ==========
  
  /**
   * Track content sharing
   */
  static trackShare(
    contentType: string,
    contentId: string,
    userId?: string
  ): void {
    const event: GTMContentEvent = {
      event: 'share',
      content_type: contentType,
      content_id: contentId,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  /**
   * Track user engagement
   */
  static trackEngagement(
    engagementTimeMs: number,
    userId?: string
  ): void {
    const event: GTMEngagementEvent = {
      event: 'user_engagement',
      engagement_time_msec: engagementTimeMs,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  // ========== REFERRAL EVENTS ==========
  
  /**
   * Track referral sent
   */
  static trackReferralSent(
    referralCode: string,
    referralMethod: string = 'email',
    userId?: string
  ): void {
    const event: GTMReferralEvent = {
      event: 'referral_sent',
      referral_code: referralCode,
      referral_method: referralMethod,
      referrer_user_id: userId ? hashUserId(userId) : undefined,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  /**
   * Track referral link clicked
   */
  static trackReferralClicked(
    referralCode: string,
    referrerUserId?: string
  ): void {
    const event: GTMReferralEvent = {
      event: 'referral_clicked',
      referral_code: referralCode,
      referrer_user_id: referrerUserId ? hashUserId(referrerUserId) : undefined
    };

    pushToDataLayer(event);
  }

  /**
   * Track referral conversion (referred user signs up)
   */
  static trackReferralConverted(
    referralCode: string,
    referrerUserId?: string,
    referredUserId?: string
  ): void {
    const event: GTMReferralEvent = {
      event: 'referral_converted',
      referral_code: referralCode,
      referrer_user_id: referrerUserId ? hashUserId(referrerUserId) : undefined,
      referred_user_id: referredUserId ? hashUserId(referredUserId) : undefined,
      user_id: referredUserId ? hashUserId(referredUserId) : undefined
    };

    pushToDataLayer(event);
  }

  /**
   * Track referral reward earned
   */
  static trackReferralRewardEarned(
    referralCode: string,
    rewardType: string,
    rewardValue: number,
    userId?: string
  ): void {
    const event: GTMReferralEvent = {
      event: 'referral_reward_earned',
      referral_code: referralCode,
      reward_type: rewardType,
      reward_value: rewardValue,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  // ========== VERSIONING EVENTS ==========
  
  /**
   * Track creative version creation
   */
  static trackCreateVersion(
    creativeId: string,
    versionId: string,
    versionNumber: number,
    parentVersionId?: string,
    projectId?: string,
    userId?: string
  ): void {
    const event: GTMVersioningEvent = {
      event: 'create_version',
      creative_id: creativeId,
      version_id: versionId,
      version_number: versionNumber,
      parent_version_id: parentVersionId,
      project_id: projectId,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  /**
   * Track version comparison
   */
  static trackCompareVersions(
    creativeId: string,
    versionId: string,
    comparisonType: string = 'side_by_side',
    projectId?: string,
    userId?: string
  ): void {
    const event: GTMVersioningEvent = {
      event: 'compare_versions',
      creative_id: creativeId,
      version_id: versionId,
      comparison_type: comparisonType,
      project_id: projectId,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  /**
   * Track version restoration
   */
  static trackRestoreVersion(
    creativeId: string,
    versionId: string,
    versionNumber: number,
    projectId?: string,
    userId?: string
  ): void {
    const event: GTMVersioningEvent = {
      event: 'restore_version',
      creative_id: creativeId,
      version_id: versionId,
      version_number: versionNumber,
      project_id: projectId,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  /**
   * Track version deletion
   */
  static trackDeleteVersion(
    creativeId: string,
    versionId: string,
    versionNumber: number,
    projectId?: string,
    userId?: string
  ): void {
    const event: GTMVersioningEvent = {
      event: 'delete_version',
      creative_id: creativeId,
      version_id: versionId,
      version_number: versionNumber,
      project_id: projectId,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }

  // ========== UTILITY METHODS ==========
  
  /**
   * Track custom event
   */
  static trackCustomEvent(
    eventName: string,
    parameters: Record<string, any> = {},
    userId?: string
  ): void {
    const event = {
      event: eventName,
      ...parameters,
      user_id: userId ? hashUserId(userId) : undefined
    };

    pushToDataLayer(event);
  }
}

// Export default instance
export default GTMService;